import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/store/useStore';
import { ArrowLeft, Plus, Trash2, Loader2, Download, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getPayRates, savePayRates, getPayrollReport,
  type PayRate, type PayAddon, type PayAddonKey, FIXED_ADDON_KEYS, type RateRole, type PayrollReportRow,
} from '@/auth/adminClient';
import { usePerms } from '@/auth/usePerms';
import { downloadTableExcel } from '@/utils/exportTable';
import { ROLE_RU } from '@/utils/workload';

/**
 * Справочник ставок на расчёт зарплаты (встреча с Аяужан 06.10.2026). Защищённый экран —
 * видят/правят только роли с правом «Ставки» (финансист/руководитель/супер-админ,
 * настраивается в «Роли и права»). Хранится одним JSONB-блобом в settings (не реляционной
 * таблицей) — заказчик предупредил, что цифры будут часто уточняться и дополняться, под
 * это гибкая структура без миграций на каждое изменение.
 *
 * Ставка = конкретный филиал × формат × длительность (минуты) × диапазон кол-ва человек ×
 * уровень × роль → сумма. У каждого филиала — свои ставки, никакой общей «группы» нет.
 * Доплаты — фиксированный список (задан заказчиком), меняется только сумма.
 */

const ROLE_LABEL: Record<RateRole, string> = {
  host: 'Ведущий 1', host2: 'Ведущий 2', dj: 'Диджей', sovedushiy: 'Соведущий', hostes: 'Хостес',
};

const ADDON_DEFS: { key: PayAddonKey; label: string; hint: string }[] = [
  { key: 'night', label: 'Ночной выезд (после 23:00)', hint: 'команде' },
  { key: 'english', label: 'Английское ведение', hint: 'ведущему' },
  { key: 'banquet', label: 'Банкетка', hint: 'каждому в команде' },
  { key: 'teamlead', label: 'Тимлид', hint: 'фикс.' },
  { key: 'distance_pct', label: 'Дальность', hint: '% от суммы доплаты за дальность, на команду' },
  { key: 'no_catering', label: 'Без кормления', hint: 'вычет, фикс.' },
  { key: 'waiting', label: 'Ожидание', hint: 'считается автоматически: 0.5 часовой ставки сотрудника за каждый час ожидания (по времени из карточки)' },
];

function emptyDraft(branch_id: string): Omit<PayRate, 'id'> {
  return { branch_id, format_id: '', role: 'host', level: 1, duration_min: 60, people_min: 1, people_max: null, amount: 0 };
}

export function PayRatesPage() {
  const setPage = useStore((s) => s.setPage);
  const game_formats = useStore((s) => s.game_formats);
  const branches = useStore((s) => s.branches);
  const toast = useStore((s) => s.toast);
  const p = usePerms();
  const allowed = p.pages.pay_rates;

  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState<PayRate[]>([]);
  const [addons, setAddons] = useState<PayAddon[]>([]);
  const [draft, setDraft] = useState(emptyDraft(''));
  const [saving, setSaving] = useState(false);
  const [openBranchId, setOpenBranchId] = useState<string | null>(null);
  const [ratesPage, setRatesPage] = useState(0);
  const RATES_PAGE_SIZE = 10;
  const [tab, setTab] = useState<'rates' | 'report'>('rates');

  const sortedBranches = useMemo(() => [...branches].sort((a, b) => a.name.localeCompare(b.name, 'ru')), [branches]);
  const ratesByBranch = useMemo(() => {
    const m = new Map<string, PayRate[]>();
    for (const r of rates) m.set(r.branch_id, [...(m.get(r.branch_id) ?? []), r]);
    return m;
  }, [rates]);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const data = await getPayRates();
        setRates(data.rates ?? []);
        setAddons(data.addons ?? []);
      } catch (e) {
        toast({ kind: 'error', text: `Не удалось загрузить ставки (${e instanceof Error ? e.message : e})` });
      } finally {
        setLoading(false);
      }
    })();
  }, [allowed]); // eslint-disable-line

  const addonByKey = useMemo(() => new Map(addons.map((a) => [a.key, a.amount])), [addons]);
  const formatName = (id: string) => game_formats.find((g) => g.id === id)?.name ?? id;

  const persist = async (nextRates: PayRate[], nextAddons: PayAddon[]) => {
    setSaving(true);
    try {
      const saved = await savePayRates({ rates: nextRates, addons: nextAddons });
      setRates(saved.rates);
      setAddons(saved.addons);
      return true;
    } catch (e) {
      toast({ kind: 'error', text: `Не сохранилось (${e instanceof Error ? e.message : e})` });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const setAddonAmount = (key: PayAddonKey, amount: number) => {
    const next = [...addons.filter((a) => a.key !== key), { key, amount }];
    setAddons(next);
  };

  // Свои доплаты (03.11.2026) — в отличие от системных 7, заводятся прямо на этом экране:
  // своё название + сумма. Применяются вручную человеку на конкретной игре (DealEditor →
  // «Люди»), название видно всем (чтобы можно было отметить), сумма — только с правом «Ставки».
  const customAddons = useMemo(() => addons.filter((a) => !FIXED_ADDON_KEYS.includes(a.key as PayAddonKey)), [addons]);
  const addCustomAddon = () => setAddons([...addons, { key: `custom_${Date.now().toString(36)}`, label: '', amount: 0 }]);
  const updateCustomAddon = (key: string, patch: Partial<PayAddon>) =>
    setAddons(addons.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  const removeCustomAddon = async (key: string) => {
    if (!window.confirm('Удалить эту доплату? Она перестанет считаться во всех играх, где отмечена.')) return;
    await persist(rates, addons.filter((a) => a.key !== key));
  };

  const saveAddons = () => void persist(rates, addons);

  const addRate = async (branchId: string) => {
    if (!draft.format_id) { toast({ kind: 'error', text: 'Выберите формат игры' }); return; }
    const row: PayRate = { ...draft, branch_id: branchId, id: `${branchId}_${draft.format_id}_${draft.role}_${draft.level}_${draft.duration_min}_${draft.people_min}_${draft.people_max ?? 'x'}_${Date.now()}` };
    const ok = await persist([...rates, row], addons);
    if (ok) { setDraft(emptyDraft(branchId)); toast({ kind: 'success', text: 'Ставка добавлена' }); }
  };

  const removeRate = async (id: string) => {
    if (!window.confirm('Удалить эту ставку?')) return;
    await persist(rates.filter((r) => r.id !== id), addons);
  };

  const toggleBranch = (branchId: string) => {
    if (openBranchId === branchId) { setOpenBranchId(null); return; }
    setOpenBranchId(branchId);
    setDraft(emptyDraft(branchId));
    setRatesPage(0);
  };

  if (!allowed) {
    return (
      <div className="h-full grid place-items-center bg-surface-0 text-ink-2 p-6 text-center">
        <div className="space-y-3 max-w-sm">
          <div className="text-lg font-semibold text-ink-1">Доступ ограничен</div>
          <p className="text-sm">Справочник ставок видят только роли с правом «Ставки» (финансист/руководитель/супер-админ).</p>
          <button className="btn-soft mx-auto" onClick={() => setPage('dashboard')}>Назад</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="h-full grid place-items-center text-ink-3"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="h-full overflow-y-auto bg-surface-0 p-4 sm:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button className="icon-btn" onClick={() => setPage('dashboard')}><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="text-lg font-bold text-ink-1">Ставки на расчёт зарплаты</h1>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-ink-3" />}
      </div>

      <div className="flex items-center gap-2 border-b border-line -mt-2">
        <button className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'rates' ? 'border-accent text-accent' : 'border-transparent text-ink-3'}`}
          onClick={() => setTab('rates')}>Ставки и доплаты</button>
        <button className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'report' ? 'border-accent text-accent' : 'border-transparent text-ink-3'}`}
          onClick={() => setTab('report')}>Отчёт по зарплате</button>
      </div>

      {tab === 'report' ? <PayrollReportSection /> : <>
      <p className="text-sm text-ink-3">
        Ставка игры «замораживается» на момент самой игры — изменение цифр здесь НЕ пересчитывает уже прошедшие игры.
      </p>

      {/* ── Доплаты ───────────────────────────────────────── */}
      <div className="panel p-4 space-y-1">
        <div className="font-semibold text-ink-1 mb-2">Доплаты</div>
        <div className="divide-y divide-line/60">
          {ADDON_DEFS.map((d) => (
            <div key={d.key} className="flex items-center gap-3 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ink-1">{d.label}</div>
                <div className="text-[11px] text-ink-3">{d.hint}</div>
              </div>
              {d.key === 'waiting' ? (
                <span className="text-xs text-ink-3 italic w-28 text-right flex-shrink-0">считается сама</span>
              ) : (
                <input
                  type="number"
                  className="input !w-28 flex-shrink-0 text-right"
                  value={addonByKey.get(d.key) ?? 0}
                  onChange={(e) => setAddonAmount(d.key, Number(e.target.value) || 0)}
                />
              )}
            </div>
          ))}
        </div>
        {customAddons.length > 0 && (
          <div className="divide-y divide-line/60 border-t border-line/60 mt-1 pt-1">
            {customAddons.map((a) => (
              <div key={a.key} className="flex items-center gap-2 py-2.5">
                <input className="input flex-1 min-w-0" placeholder="название доплаты"
                  value={a.label ?? ''} onChange={(e) => updateCustomAddon(a.key, { label: e.target.value })} />
                <input type="number" className="input !w-28 flex-shrink-0 text-right"
                  value={a.amount} onChange={(e) => updateCustomAddon(a.key, { amount: Number(e.target.value) || 0 })} />
                <button className="icon-btn !w-9 !h-9 text-ink-3 hover:text-status-refused flex-shrink-0" onClick={() => removeCustomAddon(a.key)}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-2">
          <button className="btn-primary" disabled={saving} onClick={saveAddons}>Сохранить доплаты</button>
          <button className="btn-soft" disabled={saving} onClick={addCustomAddon}><Plus className="w-4 h-4" /> Своя доплата</button>
        </div>
      </div>

      {/* ── Ставки по филиалам ───────────────────────────── */}
      <div className="space-y-2">
        <div className="font-semibold text-ink-1">Ставки по филиалам</div>
        <p className="text-[11px] text-ink-3">У каждого филиала свои ставки. Откройте филиал, чтобы посмотреть и добавить ставки именно для него.</p>
        {sortedBranches.map((b) => {
          const branchRates = ratesByBranch.get(b.id) ?? [];
          const open = openBranchId === b.id;
          const totalPages = Math.max(1, Math.ceil(branchRates.length / RATES_PAGE_SIZE));
          const curRatesPage = Math.min(ratesPage, totalPages - 1);
          const pagedRates = branchRates.slice(curRatesPage * RATES_PAGE_SIZE, curRatesPage * RATES_PAGE_SIZE + RATES_PAGE_SIZE);
          return (
            <div key={b.id} className="panel p-0 overflow-hidden">
              <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-2 text-left" onClick={() => toggleBranch(b.id)}>
                {open ? <ChevronUp className="w-4 h-4 text-ink-3 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-ink-3 flex-shrink-0" />}
                <span className="flex-1 font-medium text-ink-1">{b.name}</span>
                <span className="text-xs text-ink-3 tabular-nums">{branchRates.length ? `${branchRates.length} ставок` : 'ставок пока нет'}</span>
              </button>
              {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-line">
                  {/* ── Добавить ставку (над таблицей) ────────── */}
                  <div className="pt-3 space-y-2">
                    <div className="text-xs font-semibold text-ink-3">Добавить ставку для «{b.name}»</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <label className="field-label">Формат игры</label>
                        <select className="input" value={draft.format_id} onChange={(e) => setDraft({ ...draft, format_id: e.target.value })}>
                          <option value="">— выберите формат —</option>
                          {/* Для НОВОЙ ставки убранный в Битриксе формат не предлагаем — заводить
                              ставку под формат, которого больше нет, смысла нет. */}
                          {game_formats.filter((f) => f.is_active !== false).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="field-label">Роль (кому эта ставка)</label>
                        <select className="input" value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value as RateRole })}>
                          {(Object.keys(ROLE_LABEL) as RateRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="field-label">Уровень сотрудника</label>
                        <select className="input" value={draft.level} onChange={(e) => setDraft({ ...draft, level: Number(e.target.value) as 1 | 2 | 3 })}>
                          <option value={1}>Уровень 1</option><option value={2}>Уровень 2</option><option value={3}>Уровень 3</option>
                        </select>
                      </div>
                      <div>
                        <label className="field-label">Длительность игры, мин (60 = 1 час)</label>
                        <input className="input" type="number" placeholder="например, 60" value={draft.duration_min}
                          onChange={(e) => setDraft({ ...draft, duration_min: Number(e.target.value) || 0 })} />
                      </div>
                      <div>
                        <label className="field-label">Кол-во участников — ОТ</label>
                        <input className="input" type="number" placeholder="например, 1" value={draft.people_min}
                          onChange={(e) => setDraft({ ...draft, people_min: Number(e.target.value) || 0 })} />
                      </div>
                      <div>
                        <label className="field-label">Кол-во участников — ДО (пусто — без верхней границы)</label>
                        <input className="input" type="number" placeholder="например, 30 (или оставьте пусто)" value={draft.people_max ?? ''}
                          onChange={(e) => setDraft({ ...draft, people_max: e.target.value ? Number(e.target.value) : null })} />
                      </div>
                      <div>
                        <label className="field-label">Сумма, ₸ (сколько получает сотрудник)</label>
                        <input className="input" type="number" placeholder="например, 10000" value={draft.amount}
                          onChange={(e) => setDraft({ ...draft, amount: Number(e.target.value) || 0 })} />
                      </div>
                    </div>
                    <p className="text-[11px] text-ink-3">
                      Одна строка = одна ставка для ОДНОЙ комбинации: формат + длительность + диапазон кол-ва участников + уровень + роль.
                      Если у ведущего и диджея на эту же игру разные деньги — заводите две отдельные строки (одна роль за раз).
                    </p>
                    <button className="btn-primary" disabled={saving} onClick={() => void addRate(b.id)}><Plus className="w-4 h-4" /> Добавить ставку</button>
                  </div>

                  {/* ── Таблица ставок филиала ─────────────────── */}
                  <div className="overflow-x-auto pt-1 border-t border-line">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-ink-3 border-b border-line">
                          <th className="py-1.5 pr-2">Формат</th>
                          <th className="py-1.5 pr-2">Роль</th>
                          <th className="py-1.5 pr-2">Уровень</th>
                          <th className="py-1.5 pr-2">Длительность, мин</th>
                          <th className="py-1.5 pr-2">Кол-во человек</th>
                          <th className="py-1.5 pr-2">Сумма</th>
                          <th className="py-1.5 pr-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {branchRates.length === 0 && (
                          <tr><td colSpan={7} className="py-6 text-center text-ink-3">Ставок пока нет — добавьте первую строку выше.</td></tr>
                        )}
                        {pagedRates.map((r) => (
                          <tr key={r.id} className="border-b border-line/60">
                            <td className="py-1.5 pr-2">{formatName(r.format_id)}</td>
                            <td className="py-1.5 pr-2">{ROLE_LABEL[r.role]}</td>
                            <td className="py-1.5 pr-2 tabular-nums">{r.level}</td>
                            <td className="py-1.5 pr-2 tabular-nums">{r.duration_min}</td>
                            <td className="py-1.5 pr-2 tabular-nums">{r.people_min}{r.people_max != null ? `–${r.people_max}` : '+'}</td>
                            <td className="py-1.5 pr-2 tabular-nums font-semibold">{r.amount.toLocaleString('ru-RU')}</td>
                            <td className="py-1.5 pr-2">
                              <button className="icon-btn !w-7 !h-7 text-status-refused" onClick={() => void removeRate(r.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {branchRates.length > RATES_PAGE_SIZE && (
                    <div className="flex items-center justify-end gap-3 pt-1 text-xs text-ink-3">
                      <span className="tabular-nums">
                        {curRatesPage * RATES_PAGE_SIZE + 1}–{Math.min((curRatesPage + 1) * RATES_PAGE_SIZE, branchRates.length)} из {branchRates.length}
                      </span>
                      <div className="flex items-center gap-1">
                        <button className={`icon-btn !w-7 !h-7 ${curRatesPage === 0 ? 'opacity-40 cursor-default' : ''}`}
                          disabled={curRatesPage === 0} onClick={() => setRatesPage(curRatesPage - 1)}>‹</button>
                        <button className={`icon-btn !w-7 !h-7 ${curRatesPage >= totalPages - 1 ? 'opacity-40 cursor-default' : ''}`}
                          disabled={curRatesPage >= totalPages - 1} onClick={() => setRatesPage(curRatesPage + 1)}>›</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>}
    </div>
  );
}

// ── Отчёт по зарплате за период ─────────────────────────
function periodDefault(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 10 * 24 * 3600_000); // «раз в 10 дней» (встреча 06.10.2026)
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

function PayrollReportSection() {
  const branches = useStore((s) => s.branches);
  const toast = useStore((s) => s.toast);
  const setModal = useStore((s) => s.setModal);
  const def = useMemo(periodDefault, []);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [branchId, setBranchId] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<PayrollReportRow[] | null>(null);
  const [openRow, setOpenRow] = useState<string | null>(null);

  const load = async () => {
    const fromMs = new Date(`${from}T00:00:00`).getTime();
    const toMs = new Date(`${to}T00:00:00`).getTime() + 24 * 3600_000; // включительно по день «до»
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) { toast({ kind: 'error', text: 'Проверьте даты периода' }); return; }
    setLoading(true);
    try {
      const data = await getPayrollReport(fromMs, toMs, branchId as any);
      setRows(data);
    } catch (e) {
      toast({ kind: 'error', text: `Не удалось посчитать отчёт (${e instanceof Error ? e.message : e})` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line

  const grandTotal = useMemo(() => (rows ?? []).reduce((s, r) => s + r.total, 0), [rows]);

  const exportExcel = () => {
    if (!rows || rows.length === 0) return;
    const head = ['Сотрудник', 'Игр', 'Не посчиталось', 'Сумма'];
    const body = rows.map((r) => [r.full_name, r.games, r.unresolved, r.total]);
    downloadTableExcel(`Зарплата-${from}-${to}.xlsx`, head, body);
    toast({ kind: 'success', text: 'Отчёт выгружен' });
  };

  return (
    <div className="space-y-3">
      <div className="panel p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="field-label">С</label>
          <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="field-label">По</label>
          <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <label className="field-label">Филиал</label>
          <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="all">Все филиалы</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <button className="btn-primary" disabled={loading} onClick={() => void load()}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Посчитать
        </button>
        <button className="btn-soft" disabled={!rows || rows.length === 0} onClick={exportExcel}><Download className="w-4 h-4" /> Excel</button>
        <div className="flex-1" />
        {rows && <div className="text-right"><div className="text-xl font-bold text-ink-1 tabular-nums">{grandTotal.toLocaleString('ru-RU')} ₸</div><div className="text-[11px] text-ink-3">итого за период</div></div>}
      </div>

      <div className="panel p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-3"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
        ) : !rows || rows.length === 0 ? (
          <div className="p-8 text-center text-ink-3">Проведённых игр за этот период не нашлось.</div>
        ) : (
          <div className="divide-y divide-line/60">
            {rows.map((r) => (
              <div key={r.employee_id}>
                <button className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2 text-left"
                  onClick={() => setOpenRow(openRow === r.employee_id ? null : r.employee_id)}>
                  {openRow === r.employee_id ? <ChevronUp className="w-4 h-4 text-ink-3 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-ink-3 flex-shrink-0" />}
                  <span className="flex-1 text-sm font-medium text-ink-1 truncate">{r.full_name}</span>
                  <span className="text-xs text-ink-3 tabular-nums">{r.games} игр</span>
                  {r.unresolved > 0 && <span className="text-xs text-amber-600 tabular-nums">не посчиталось: {r.unresolved}</span>}
                  <span className="text-sm font-semibold text-ink-1 tabular-nums w-[110px] text-right">{r.total.toLocaleString('ru-RU')} ₸</span>
                </button>
                {openRow === r.employee_id && (
                  <div className="px-4 pb-3 space-y-1">
                    {r.items.map((it, i) => (
                      <button key={`${it.deal_id}-${i}`} onClick={() => setModal({ kind: 'deal', id: it.deal_id })}
                        className="w-full flex items-center gap-3 text-xs px-2 py-1.5 rounded hover:bg-surface-2 text-left">
                        <span className="text-ink-3 w-[80px] flex-shrink-0">{new Date(it.date).toLocaleDateString('ru-RU')}</span>
                        <span className="text-accent w-[70px] flex-shrink-0">#{it.number ?? '—'}</span>
                        <span className="text-ink-2 w-[90px] flex-shrink-0">{ROLE_RU[it.role_key] ?? it.role_key}</span>
                        <span className="flex-1" />
                        {it.ok ? (
                          <span className="font-semibold tabular-nums">{it.total?.toLocaleString('ru-RU')} ₸</span>
                        ) : (
                          <span className="text-amber-600 text-right">{it.reason ?? 'не посчиталось'}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
